import React, { useState } from "react";
import { Grid, Typography, IconButton, Box, Stack, Container, Paper, useMediaQuery } from "@mui/material";
import { styled } from "@mui/material/styles";
import { motion } from "framer-motion";
import { useTheme } from "@mui/material/styles";
import MapIcon from "@mui/icons-material/Map";
import InstagramIcon from "@mui/icons-material/Instagram";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import TranslateIcon from "@mui/icons-material/Translate";
import ContactForm from "./ContactForm";
import ContactPopup from "./Dialog/ContactPopup";
import PrivacyPolicy from "../Nav/PrivacyPolicy";
import { useInView } from "react-intersection-observer";
import { colors } from "../../Theme/theme";
import { FIRM } from "../../constants/firm";

const ContactSection = styled(Box)(({ theme }) => ({
	position: "relative",
	overflow: "hidden",
	backgroundColor: "#f5f6f8",
	marginTop: "-1px",
	borderTop: `16px solid ${colors.primary.main}`,
	"&::before": {
		content: '""',
		position: "absolute",
		right: 0,
		top: 0,
		width: "16px",
		height: "100%",
		backgroundColor: colors.primary.main,
	},
}));

const ContactPaper = styled(Paper)(({ theme }) => ({
	padding: theme.spacing(6),
	borderRadius: 0,
	backgroundColor: "rgba(255, 255, 255, 0.95)",
	boxShadow: "0 8px 32px rgba(26, 71, 137, 0.1)",
	position: "relative",
	overflow: "hidden",
	height: "100%",
	display: "flex",
	flexDirection: "column",
	justifyContent: "space-between",
	"&::before": {
		content: '""',
		position: "absolute",
		top: 0,
		left: 0,
		width: "5px",
		height: "100%",
		background: `linear-gradient(to bottom, ${colors.primary.main}, ${colors.primary.light})`,
	},
}));

const ContactHeaderWrapper = styled(Box)(({ theme }) => ({
	position: "relative",
	marginBottom: theme.spacing(4),
}));

const ContactHeaderUnderline = styled(Box)(() => ({
	position: "absolute",
	bottom: -8,
	left: 0,
	width: 80,
	height: 4,
	background: `linear-gradient(90deg, ${colors.accent.main}, rgba(250, 212, 97, 0.5))`,
	borderRadius: "2px",
}));

const InfoIcon = styled(Box)(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	justifyContent: "center",
	width: 40,
	height: 40,
	borderRadius: "50%",
	backgroundColor: "rgba(26, 71, 137, 0.1)",
	marginRight: theme.spacing(2),
	color: colors.primary.main,
}));

const SocialButton = styled(IconButton)(({ theme }) => ({
	backgroundColor: "rgba(26, 71, 137, 0.1)",
	color: colors.primary.main,
	margin: theme.spacing(1),
	borderRadius: 0,
	transition: "all 0.2s ease",
	"&:hover": {
		backgroundColor: colors.primary.main,
		color: "#ffffff",
		transform: "translateY(-2px)",
	},
}));

const GoldDivider = styled(Box)(() => ({
	width: "64px",
	height: "4px",
	background: `linear-gradient(90deg, ${colors.accent.main}, ${colors.accent.light})`,
	marginBottom: "28px",
}));

const ContactPageMD = () => {
	const theme = useTheme();
	const matches = useMediaQuery(theme.breakpoints.up("md"));
	const [open, setOpen] = useState(false);
	const [privacyOpen, setPrivacyOpen] = useState(false);
	const [ref, inView] = useInView({
		threshold: 0.1,
		triggerOnce: true,
	});

	const handleClickOpen = () => setOpen(true);
	const handleClose = () => setOpen(false);
	const handlePrivacyClose = () => setPrivacyOpen(false);

	return (
		<ContactSection id="Contact" ref={ref}>
			<Container maxWidth="xl" sx={{ position: "relative", zIndex: 2, py: 10 }}>
				<Grid container spacing={4}>
					{/* Section header */}
					<Grid item xs={12}>
						<Box sx={{ mb: 4, pl: { xs: 2, md: 2, lg: 4, xl: 8 } }}>
							<motion.div
								initial={{ opacity: 0, y: -20 }}
								animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: -20 }}
								transition={{ duration: 0.8 }}
							>
								<Typography
									sx={{
										fontSize: "0.78rem",
										fontWeight: 700,
										letterSpacing: "0.18em",
										color: colors.primary.main,
										textTransform: "uppercase",
										mb: 2,
										fontFamily: "'Heebo', sans-serif",
									}}
								>
									Free Consultations
								</Typography>
								<Typography
									variant="h2"
									sx={{
										color: colors.primary.main,
										fontWeight: 700,
										fontSize: "clamp(2.8rem, 4.5vw, 5rem)",
										letterSpacing: "0.02em",
										textTransform: "none",
										fontFamily: "Georgia, 'Times New Roman', serif",
										lineHeight: 1.2,
										mb: 2,
									}}
								>
									Get In Touch
								</Typography>
							</motion.div>

							<motion.div
								initial={{ opacity: 0, scaleX: 0 }}
								animate={inView ? { opacity: 1, scaleX: 1 } : { opacity: 0, scaleX: 0 }}
								transition={{ duration: 0.55, delay: 0.25, ease: "easeOut" }}
								style={{ transformOrigin: "left" }}
							>
								<GoldDivider />
							</motion.div>

							<motion.div
								initial={{ opacity: 0 }}
								animate={inView ? { opacity: 1 } : { opacity: 0 }}
								transition={{ duration: 1, delay: 0.35 }}
							>
								<Typography
									variant="body1"
									sx={{
										maxWidth: 640,
										lineHeight: 1.8,
										fontSize: "1.05rem",
										color: "#4a5568",
										fontFamily: "'Heebo', sans-serif",
									}}
								>
									We understand the importance of clear communication, and we strive to ensure that
									our clients understand their legal options every step of the way. Don't navigate the
									legal system alone; contact us today for a free injury case consultation.
								</Typography>
							</motion.div>
						</Box>
					</Grid>

					{/* Contact Form */}
					<Grid item xs={12} md={7} lg={8}>
						<motion.div
							initial={{ opacity: 0, x: -30 }}
							animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -30 }}
							transition={{ duration: 0.8, delay: 0.2 }}
						>
							<ContactPaper elevation={0}>
								<ContactHeaderWrapper>
									<Typography
										variant="h4"
										color={colors.primary.main}
										fontWeight={600}
										sx={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
									>
										Free Consultations
									</Typography>
									<ContactHeaderUnderline />
								</ContactHeaderWrapper>

								<Typography variant="body1" sx={{ mb: 4, color: "#4a5568" }}>
									Tell us about your case, and we'll get back to you promptly to discuss how we can
									help.
								</Typography>

								<ContactForm setOpen={setOpen} onPrivacyClick={() => setPrivacyOpen(true)} />
							</ContactPaper>
						</motion.div>
					</Grid>

					{/* Contact Information */}
					<Grid item xs={12} md={5} lg={4}>
						<motion.div
							initial={{ opacity: 0, x: 30 }}
							animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 30 }}
							transition={{ duration: 0.8, delay: 0.3 }}
						>
							<ContactPaper elevation={0}>
								<Box>
									<ContactHeaderWrapper>
										<Typography
											variant="h4"
											color={colors.primary.main}
											fontWeight={600}
											sx={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
										>
											Contact Information
										</Typography>
										<ContactHeaderUnderline />
									</ContactHeaderWrapper>

									<Box sx={{ mb: 4 }}>
										<Typography
											variant="h6"
											color={colors.primary.main}
											fontWeight={500}
											sx={{ mb: 2 }}
										>
											Office Address
										</Typography>
										<Stack direction="row" spacing={2} sx={{ mb: 1 }}>
											<Box
												sx={{
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													width: 40,
													height: 40,
												}}
											>
												<SocialButton
													aria-label="location"
													href={FIRM.social.googleMaps}
													target="_blank"
												>
													<MapIcon fontSize="small" />
												</SocialButton>
											</Box>
											<Box>
												<Typography color="#333333" fontWeight={500}>
													{FIRM.address.street}
												</Typography>
												<Typography color="#333333">
													{FIRM.address.city}, {FIRM.address.state} {FIRM.address.zip}
												</Typography>
											</Box>
										</Stack>
									</Box>

									<Box sx={{ mb: 4 }}>
										<Typography
											variant="h6"
											color={colors.primary.main}
											fontWeight={500}
											sx={{ mb: 2 }}
										>
											Contact Details
										</Typography>
										<Stack direction="row" spacing={2} sx={{ mb: 2 }}>
											<InfoIcon>
												<PhoneIcon fontSize="small" />
											</InfoIcon>
											<Box>
												<Typography color="#666666" variant="body2">
													Phone
												</Typography>
												<a
													href={FIRM.phone.href}
													style={{ textDecoration: "none", color: colors.primary.main }}
												>
													<Typography fontWeight={500}>{FIRM.phone.display}</Typography>
												</a>
											</Box>
										</Stack>
										<Stack direction="row" spacing={2}>
											<InfoIcon>
												<EmailIcon fontSize="small" />
											</InfoIcon>
											<Box>
												<Typography color="#666666" variant="body2">
													Fax
												</Typography>
												<Typography fontWeight={500} color="#333333">
													{FIRM.fax.display}
												</Typography>
											</Box>
										</Stack>
									</Box>

									<Box>
										<Typography
											variant="h6"
											color={colors.primary.main}
											fontWeight={500}
											sx={{ mb: 2 }}
										>
											Follow Us
										</Typography>
										<Stack direction="row">
											<SocialButton
												aria-label="instagram"
												href={FIRM.social.instagram}
												target="_blank"
											>
												<InstagramIcon fontSize="small" />
											</SocialButton>
											<SocialButton
												aria-label="location"
												href={FIRM.social.googleMaps}
												target="_blank"
											>
												<MapIcon fontSize="small" />
											</SocialButton>
										</Stack>
									</Box>
								</Box>

								<Box
									sx={{
										display: "flex",
										alignItems: "center",
										gap: 1.5,
										mt: 4,
										px: 2,
										py: 1.5,
										backgroundColor: "rgba(250, 212, 97, 0.1)",
										border: `1px solid rgba(230, 192, 84, 0.5)`,
										borderLeft: `4px solid #FAD461`,
									}}
								>
									<TranslateIcon sx={{ fontSize: "1.2rem", color: "#e6c054", flexShrink: 0 }} />
									<Box>
										<Typography
											sx={{
												fontSize: "0.9rem",
												fontWeight: 700,
												color: colors.primary.darker,
												lineHeight: 1.25,
												fontFamily: "'Heebo', sans-serif",
											}}
										>
											Hablamos Español
										</Typography>
										<Typography
											sx={{
												fontSize: "0.68rem",
												color: "#4a5568",
												letterSpacing: "0.06em",
												textTransform: "uppercase",
												fontFamily: "'Heebo', sans-serif",
											}}
										>
											servicio en español disponible
										</Typography>
									</Box>
								</Box>

								<Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mt: 4 }}>
									<Box
										component="img"
										src="/DRLawLogo2025.png"
										alt="David Rhee Injury Law"
										sx={{
											width: "100%",
											maxWidth: "220px",
											height: "auto",
											filter: "drop-shadow(0px 10px 15px rgba(26, 71, 137, 0.2))",
										}}
									/>
								</Box>
							</ContactPaper>
						</motion.div>
					</Grid>
				</Grid>
			</Container>

			<ContactPopup handleClickOpen={handleClickOpen} handleClose={handleClose} open={open} />
			<PrivacyPolicy open={privacyOpen} handleClose={handlePrivacyClose} />
		</ContactSection>
	);
};

export default ContactPageMD;
